import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { appConfig } from '@/config/app';
import { locale } from '@/locales';
import {
    getModalActivateDelayMs,
    getModalAnimationDurationMs,
    modalConfig,
} from '@/config/modal';

let openModalCount = 0;

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    maxHeight?: string;
    zIndex?: number;
    showHandle?: boolean;
    surfaceVariant?: 'default' | 'plain';
    ariaLabel?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const toggleAppRootAriaHidden = (isHidden: boolean): void => {
    const appRoot = document.querySelector<HTMLElement>(appConfig.ui.modal.appRootSelector);

    if (!appRoot) {
        return;
    }

    if (isHidden) {
        appRoot.setAttribute('aria-hidden', 'true');
        return;
    }

    appRoot.removeAttribute('aria-hidden');
};

const DEFAULT_MAX_HEIGHT =
    `calc(${modalConfig.layout.defaultMaxHeight} - var(--app-safe-area-top, 0px) - ${modalConfig.layout.safeTopOffsetPx}px)`;

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    maxHeight = DEFAULT_MAX_HEIGHT,
    zIndex = modalConfig.layout.baseZIndex,
    showHandle = true,
    surfaceVariant = 'default',
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
}) => {
    const uiColors = appConfig.ui.colors;
    const uiModal = appConfig.ui.modal;

    const [isVisible, setIsVisible] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const weLockedIt = useRef(false);
    const dragPointerId = useRef<number | null>(null);
    const dragStartY = useRef(0);
    const dragStartTs = useRef(0);
    const sheetRef = useRef<HTMLDivElement | null>(null);
    const previousFocusedElementRef = useRef<HTMLElement | null>(null);
    const hasRegisteredModalRef = useRef(false);
    const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationDurationMs = getModalAnimationDurationMs(prefersReducedMotion);
    const activateDelayMs = getModalActivateDelayMs(prefersReducedMotion);

    const clampDragOffset = useCallback((offset: number): number => {
        return Math.max(0, Math.min(offset, modalConfig.gesture.maxDragOffsetPx));
    }, []);

    const resetDragState = useCallback(() => {
        setIsDragging(false);
        setDragOffsetY(0);
        dragPointerId.current = null;
    }, []);

    const handleDragEnd = useCallback((pointerId: number, clientY: number) => {
        if (dragPointerId.current !== pointerId) {
            return;
        }

        const deltaY = clampDragOffset(clientY - dragStartY.current);
        const elapsedMs = Math.max(performance.now() - dragStartTs.current, 1);
        const velocityY = deltaY / elapsedMs;
        const shouldClose =
            deltaY >= modalConfig.gesture.closeDistancePx ||
            velocityY >= modalConfig.gesture.closeVelocityPxPerMs;

        resetDragState();

        if (shouldClose) {
            onClose();
        }
    }, [clampDragOffset, onClose, resetDragState]);

    const handleHandlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isActive) {
            return;
        }

        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }

        dragPointerId.current = event.pointerId;
        dragStartY.current = event.clientY;
        dragStartTs.current = performance.now();
        setIsDragging(true);
        setDragOffsetY(0);
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging || dragPointerId.current !== event.pointerId) {
            return;
        }

        const offset = clampDragOffset(event.clientY - dragStartY.current);
        setDragOffsetY(offset);
    };

    const handleHandlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (dragPointerId.current !== event.pointerId) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        handleDragEnd(event.pointerId, event.clientY);
    };

    const handleHandlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
        if (dragPointerId.current !== event.pointerId) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        resetDragState();
    };

    const handleLostPointerCapture = () => {
        if (dragPointerId.current !== null) {
            resetDragState();
        }
    };

    const sheetTranslateY = !isActive ? '100%' : `${dragOffsetY}px`;
    const sheetTransitionDurationMs = isDragging ? 0 : animationDurationMs;
    const sheetSurfaceClassName = surfaceVariant === 'plain'
        ? 'bg-transparent rounded-none overflow-visible'
        : `${uiModal.sheetRoundedTop} ${uiColors.pageBg} overflow-hidden`;

    const focusFirstElement = useCallback(() => {
        const sheet = sheetRef.current;
        if (!sheet) {
            return;
        }

        const focusableElements = Array.from(
            sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);

        if (focusableElements[0]) {
            focusableElements[0].focus();
            return;
        }

        sheet.focus();
    }, []);

    useEffect(() => {
        if (isOpen && !hasRegisteredModalRef.current) {
            openModalCount += 1;
            hasRegisteredModalRef.current = true;

            if (openModalCount === 1) {
                toggleAppRootAriaHidden(true);
            }
        }

        if (!isOpen && hasRegisteredModalRef.current) {
            openModalCount = Math.max(0, openModalCount - 1);
            hasRegisteredModalRef.current = false;

            if (openModalCount === 0) {
                toggleAppRootAriaHidden(false);
            }
        }

        return () => {
            if (!hasRegisteredModalRef.current) {
                return;
            }

            openModalCount = Math.max(0, openModalCount - 1);
            hasRegisteredModalRef.current = false;

            if (openModalCount === 0) {
                toggleAppRootAriaHidden(false);
            }
        };
    }, [isOpen]);

    const restorePreviousFocus = useCallback(() => {
        previousFocusedElementRef.current?.focus();
    }, []);

    useEffect(() => {
        let timeoutId: number | null = null;

        if (isOpen) {
            previousFocusedElementRef.current = document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;

            // Check if already locked
            const isLocked = document.body.style.position === 'fixed';

            if (!isLocked) {
                // Save current scroll position
                const scrollY = window.scrollY;
                document.body.style.setProperty('--scroll-position', `${scrollY}px`);

                // Lock body
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.width = '100%';
                document.body.style.overflow = 'hidden';
                document.documentElement.style.overflow = 'hidden'; // Lock html too

                weLockedIt.current = true;
            }

            setIsVisible(true);
            timeoutId = window.setTimeout(() => setIsActive(true), activateDelayMs);
        } else {
            resetDragState();
            setIsActive(false);
            timeoutId = window.setTimeout(() => {
                setIsVisible(false);

                if (weLockedIt.current) {
                    const savedScrollY = document.body.style.getPropertyValue('--scroll-position');
                    const scrollY = parseInt(savedScrollY) || 0;

                    document.body.style.position = '';
                    document.body.style.top = '';
                    document.body.style.width = '';
                    document.body.style.overflow = '';
                    document.documentElement.style.overflow = '';
                    document.body.style.removeProperty('--scroll-position');

                    window.scrollTo(0, scrollY);
                    weLockedIt.current = false;
                }

                restorePreviousFocus();
            }, animationDurationMs);
        }

        return () => {
            if (timeoutId) window.clearTimeout(timeoutId);
            // Cleanup only if we own the lock
            if (isOpen && weLockedIt.current) {
                const savedScrollY = document.body.style.getPropertyValue('--scroll-position');
                const scrollY = parseInt(savedScrollY) || 0;

                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                document.documentElement.style.overflow = '';
                document.body.style.removeProperty('--scroll-position');

                window.scrollTo(0, scrollY);
                weLockedIt.current = false;
            }
        };
    }, [activateDelayMs, animationDurationMs, isOpen, resetDragState, restorePreviousFocus]);

    useEffect(() => {
        if (!isOpen || !isActive) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            focusFirstElement();
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [focusFirstElement, isActive, isOpen]);

    const handleSheetKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const sheet = sheetRef.current;
        if (!sheet) {
            return;
        }

        const focusableElements = Array.from(
            sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);

        if (focusableElements.length === 0) {
            event.preventDefault();
            sheet.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }

        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        }
    };

    if (!isVisible) return null;

    return ReactDOM.createPortal(
        <>
            {/* Overlay */}
            <div
                className={`fixed inset-0 bg-black/60 transition-opacity ease-in-out ${isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                style={{ zIndex: zIndex, touchAction: 'none', transitionDuration: `${animationDurationMs}ms` }}
                onClick={onClose}
                onTouchMove={(e) => {
                    // Prevent touch move on overlay to stop scroll propagation
                    e.preventDefault();
                    e.stopPropagation();
                }}
            />

            {/* Modal Content */}
            <div
                className={`fixed bottom-0 left-0 right-0 flex flex-col ${sheetSurfaceClassName} transition-transform ease-out transform`}
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? locale.common.dialogLabel)}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                tabIndex={-1}
                style={{
                    maxHeight,
                    zIndex: zIndex + 1,
                    transitionDuration: `${sheetTransitionDurationMs}ms`,
                    transform: `translateY(${sheetTranslateY})`,
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleSheetKeyDown}
            >
                {showHandle ? (
                    <div
                        className="flex justify-center shrink-0"
                        style={{
                            touchAction: 'none',
                            userSelect: 'none',
                            paddingTop: `${modalConfig.layout.swipeHandleTopPaddingPx}px`,
                            paddingBottom: `${modalConfig.layout.swipeHandleBottomPaddingPx}px`,
                        }}
                        onPointerDown={handleHandlePointerDown}
                        onPointerMove={handleHandlePointerMove}
                        onPointerUp={handleHandlePointerUp}
                        onPointerCancel={handleHandlePointerCancel}
                        onLostPointerCapture={handleLostPointerCapture}
                    >
                        <div className={`${uiModal.handleRounded} ${uiColors.modalHandleBg} ${uiModal.handleSize}`} />
                    </div>
                ) : null}

                {/* Inner Content */}
                <div className="overflow-y-auto flex-1">
                    {children}
                </div>
            </div>
        </>,
        document.body
    );
};
